import {
  Divider, ErrorMessages, IconButton, IconButtonType, Page, Spinner
} from "@whitespace/whitespace-ui";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Breadcrumb,
  Button, Card, Col, Container, Form, FormGroup, Row
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { useCallback, useEffect, useState } from "react";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import { TimePickerComponent } from "@syncfusion/ej2-react-calendars";

import "./EventsAndIncidents.scss";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import IAddNotableEventIncidentRequestModel from "interfaces/Request/IAddNotableEventIncidentRequestModel";
import notableEventIncidentSchema from "modules/schemas/notableEventIncidentSchema";

const AddEventAndIncident = () => {
  const navigate = useNavigate();
  const { hwrcSiteId, diaryDate } = useParams();
  const returnUrl = `${RoutePathsEnum.SiteDiaryLandingPage}${hwrcSiteId && diaryDate ? `/${hwrcSiteId}/${diaryDate}` : ""}`;
  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [addButtonDisabled, setAddButtonDisabled] = useState<boolean>(true);
  const [timestampRef, setTimestampRef] = useState<TimePickerComponent | undefined>();
  const [initialTimeIn] = useState<Date>(new Date());

  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues
  } = useForm<IAddNotableEventIncidentRequestModel>({
    resolver: yupResolver(notableEventIncidentSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const {
    isSending,
    errors: apiErrors,
    sendRequest,
    statusCode,
    resetState
  } = useAmplifyREST();

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const handleCreateNotableEventIncidentClick = (request : IAddNotableEventIncidentRequestModel) => {
    request.timestamp = timestampRef!.value.toISOString();
    resetState();
    sendRequest(`hwrcsitediary/hwrcsite/${hwrcSiteId}/diary/${diaryDate}/eventincident`, "POST", request);
  };

  const canFormBeSubmitted = () => {
    const mandatoryFieldsEntered = (getValues("notes").length > 0 && timestampRef?.value != null);
    setAddButtonDisabled(!mandatoryFieldsEntered);
  };

  useEffect(() => {
    if (statusCode === 200) {
      navigate(returnUrl);
    }
  }, [statusCode]);

  useEffect(() => {
    if (statusCode === 401) {
      navigate(RoutePathsEnum.Unauthorised);
    }
  }, [statusCode]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />
      <Card>
        <Card.Header>
          <h2 data-testid="card-header">
            Add Notable Events and Incidents Entry
            <Spinner
              animation="grow"
              visible={isSending}
              spinnerText="Loading"
              variant="info"
            />
          </h2>
        </Card.Header>
        <Card.Body className="no-padding">
          <Form
            autoComplete="off"
            onSubmit={handleSubmit(handleCreateNotableEventIncidentClick)}
          >
            <Row className="d-lg-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="breadcrumb-manage-user left-margin-large no-wrap">
                  <Breadcrumb.Item
                    data-testid="breadcrumb-diary"
                    linkAs={Link}
                    linkProps={{ to: returnUrl }}
                    onClick={() => navigate(returnUrl)}
                  >
                    Diary
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active data-testid="breadcrumb-add-notable-event-incident">
                    Add Notable Events and Incidents
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>

              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={addButtonDisabled || isSending}
                  aria-disabled={addButtonDisabled || isSending}
                  type="submit"
                  spin={isSending}
                  data-testid="create"
                  aria-label="Create event or incident"
                >
                  Create
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => navigate(returnUrl)}
                  data-testid="cancel"
                >
                  Cancel
                </Button>
              </Col>
            </Row>

            <Divider />
            <section className="event-incident pt-2">
              <Container fluid>
                <Row>
                  <div className="control-pane">
                    <div className="control-section" />

                    <Row className="mt-3">
                      <div className="w-auto">
                        <FormGroup as={Row} className="mb-6 d-flex time" controlId="timestamp">
                          <Form.Label id="timestampLabel" className="d-inline-flex horiz-form-label w-auto mb-0 me-3">
                            Time:
                          </Form.Label>
                          <div className="ml-1 mt-1">
                            <TimePickerComponent
                              ref={(t: TimePickerComponent) => { setTimestampRef(t); }}
                              data-testid="timestamp"
                              id="timestamp"
                              type="text"
                              htmlAttributes={{ "aria-labelledby": "timestampLabel", "aria-required": "true" }}
                              format="HH:mm"
                              step={15}
                              value={initialTimeIn}
                              onChange={() => { canFormBeSubmitted(); }}
                              showClearButton={false}
                            />
                          </div>
                        </FormGroup>
                      </div>
                    </Row>
                    <Row className="mt-3 mb-3">
                      <FormGroup as={Row} controlId="notes">
                        <Form.Label id="notesLabel" className="horiz-form-label">
                          Details:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-labelledby="notesLabel"
                            aria-invalid={ariaInvalidCheck(
                              "notes",
                              errors,
                              null
                            )}
                            maxLength={50}
                            minLength={1}
                            onKeyUp={() => canFormBeSubmitted()}
                            {...register("notes")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="notes"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>

                  </div>
                </Row>
              </Container>

            </section>

          </Form>
        </Card.Body>
      </Card>
    </Page>
  );
};

export default AddEventAndIncident;
